import { ElevatedSurface } from "@carbon6/c6-frontend-design";
import { CopyButton } from "@/shared/ui/CopyButton";
import { formatBytes } from "@/shared/utils/format/bytes";
import { formatDateTime } from "@/shared/utils/datetime/format";
import { StatusChip } from "@/shared/ui/status";
import type { Dataset } from "../../domain/dataset";
import type { Revision } from "../../domain/probe";
import {
  formatDatasetFormat,
  formatSourceKind,
  formatVisibility,
  formatProbeLockReason,
  shortDigest,
} from "../../feature/datasetFormatters";
import styles from "./DetailCard.module.css";

type DatasetSummaryCardProps = {
  dataset: Dataset;
  /** 확보된 Revision. 아직 없으면 다음 행동을 안내한다. */
  revision: Revision | null;
};

const REVISION_KIND_LABEL: Record<Revision["kind"], string> = {
  listing_digest: "listing digest",
  commit_sha: "commit SHA",
};

/** Dataset 한 건의 사실을 정의 목록으로 그린다. */
export function DatasetSummaryCard({ dataset, revision }: DatasetSummaryCardProps) {
  return (
    <>
      <ElevatedSurface as="section" className={styles.card} elevation={1} hoverElevation={false}>
        <h2 className={styles.title}>개요</h2>
        <dl className={styles.list}>
          <dt className={styles.term}>원본 주소</dt>
          <dd className={styles.value}>
            {dataset.sourceRef}
            <CopyButton label="원본 주소" value={dataset.sourceRef} />
          </dd>

          <dt className={styles.term}>종류</dt>
          <dd className={styles.value}>{formatSourceKind(dataset.sourceKind)}</dd>

          <dt className={styles.term}>포맷</dt>
          <dd className={styles.value}>
            {formatDatasetFormat(dataset.format, dataset.formatVersion)}
            {dataset.canProbe ? null : <StatusChip tone="muted">Probe 불가</StatusChip>}
          </dd>

          <dt className={styles.term}>등록</dt>
          <dd className={styles.value}>{formatDateTime(dataset.createdAtMs)}</dd>

          <dt className={styles.term}>노출</dt>
          <dd className={styles.value}>{formatVisibility(dataset.hiddenAtMs)}</dd>

          <dt className={styles.term}>Dataset UUID</dt>
          <dd className={styles.value}>
            {dataset.uuid}
            <CopyButton label="Dataset UUID" value={dataset.uuid} />
          </dd>
        </dl>
        {dataset.canProbe ? null : (
          <p className={styles.caption}>{formatProbeLockReason(dataset.formatDetail)}</p>
        )}
      </ElevatedSurface>

      <ElevatedSurface as="section" className={styles.card} elevation={1} hoverElevation={false}>
        <h2 className={styles.title}>현재 Revision</h2>
        {revision === null ? (
          <p className={styles.empty}>
            아직 확보된 Revision 이 없습니다. `Probe 요청` 으로 앞 episode 의 샘플을 확보합니다.
          </p>
        ) : (
          <>
            <dl className={styles.list}>
              <dt className={styles.term}>식별자</dt>
              <dd className={styles.value}>
                {shortDigest(revision.identifier)}
                <CopyButton label="Revision 식별자" value={revision.identifier} />
              </dd>

              <dt className={styles.term}>종류</dt>
              <dd className={styles.value}>{REVISION_KIND_LABEL[revision.kind]}</dd>

              <dt className={styles.term}>파일 수</dt>
              <dd className={styles.value}>{revision.fileCount}개</dd>

              <dt className={styles.term}>크기</dt>
              <dd className={styles.value}>{formatBytes(revision.totalBytes)}</dd>

              <dt className={styles.term}>열거 시각</dt>
              <dd className={styles.value}>{formatDateTime(revision.listedAtMs)}</dd>
            </dl>
            <p className={styles.caption}>
              파일 수와 크기는 원본 주소 전체의 값입니다. 확보한 샘플의 크기가 아닙니다.
            </p>
          </>
        )}
      </ElevatedSurface>
    </>
  );
}
